import React from "react";
import { ChevronDown } from "lucide-react";

import type { GlossaryTerm } from "@/components/exercises/exercise.interfaces";
import { cn } from "@/lib/utils";

export type GlossaryTermBoxProps = {
  term: GlossaryTerm;
  isExpanded: boolean;
  onToggle: (termId: string) => void;
};

export const GlossaryTermBox = React.memo(function GlossaryTermBox({
  term,
  isExpanded,
  onToggle,
}: GlossaryTermBoxProps) {
  return (
    <button
      type="button"
      onClick={() => onToggle(term.id)}
      aria-expanded={isExpanded}
      aria-controls={`glossary-definition-${term.id}`}
      aria-label={
        isExpanded
          ? `Ocultar tradução de ${term.term}`
          : `Ver tradução de ${term.term}`
      }
      className={cn(
        "relative box-border w-full min-w-0 min-h-11 self-start rounded-lg border border-solid bg-card p-4 text-left",
        "transition-[background-color,border-color] duration-200 ease-out motion-reduce:transition-none",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        "active:bg-accent/30 motion-reduce:active:bg-transparent",
        isExpanded
          ? "z-[1] border-border bg-accent/40"
          : "border-border hover:border-foreground/20 hover:bg-accent/20"
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <span className="break-words font-semibold text-foreground">{term.term}</span>
        <ChevronDown
          className={cn(
            "mt-0.5 h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 ease-out motion-reduce:transition-none",
            isExpanded && "rotate-180"
          )}
          aria-hidden
        />
      </div>
      {isExpanded && (
        <div
          id={`glossary-definition-${term.id}`}
          className="mt-3 border-t border-border pt-3"
        >
          <p className="mb-1 text-xs font-medium text-foreground/70">Tradução</p>
          <p className="break-words text-sm leading-relaxed text-foreground/85 text-pretty">
            {term.definition}
          </p>
        </div>
      )}
    </button>
  );
});
